import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Button, Modal, ModalDialog } from '@mui/joy';
import { Backdrop, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { postFetch } from '../../../../../fetch/fetch';
import { englishLocationToKorean } from '../../../../../data/locationFuntion';
import totalLocationData from '../../../../../data/locationdata';

const getTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// 비율 값에 색상 적용하는 renderCell 팩토리
const makeRateCell = (avgRef: { value: number }, bold = false) => (params: any) => {
    if (params.row.isTotalRow) {
        return <span style={{ fontWeight: 'bold' }}>{params.value != null ? params.value + '%' : '-'}</span>;
    }
    if (params.value == null) return <span>-</span>;
    const diff = params.value - avgRef.value;
    const color = diff >= 5 ? '#2e7d32' : diff <= -5 ? '#c62828' : 'inherit';
    return (
        <span style={{ color, fontWeight: bold ? 'bold' : 'normal' }}>
            {params.value}%
        </span>
    );
};

const ConsultStatsModal: React.FC<any> = (props) => {
    const { open, onClose, type, user } = props;

    const [startDate, setStartDate] = useState(getMonthStart());
    const [endDate, setEndDate] = useState(getTodayStr());
    const [locationFilter, setLocationFilter] = useState('total');
    const [rawData, setRawData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // user academy 기준으로 필터된 지점 목록
    const filteredLocations = useMemo(() => {
        return totalLocationData.filter((l: any) =>
            l.english !== 'total' &&
            Array.isArray(l.academy) &&
            l.academy.includes(user?.academy)
        );
    }, [user?.academy]);

    const fetchStats = async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        try {
            const result: any = await postFetch('/dashboard/report/visitConsultStats', { startDate, endDate });
            if (result.message === 'success') setRawData(result.data);
        } catch (e) {}
        setLoading(false);
    };

    useEffect(() => {
        if (open) fetchStats();
    }, [open, startDate, endDate]);

    // 빠른 선택 헬퍼
    const setQuick = (preset: string) => {
        const today = new Date();
        const y = today.getFullYear();
        const m = today.getMonth();
        const d = today.getDate();
        const pad = (n: number) => String(n).padStart(2, '0');
        const fmt = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

        if (preset === 'week') {
            const mon = new Date(today);
            mon.setDate(d - today.getDay() + 1);
            setStartDate(fmt(mon));
            setEndDate(fmt(today));
        } else if (preset === 'month') {
            setStartDate(`${y}-${pad(m + 1)}-01`);
            setEndDate(fmt(today));
        } else if (preset === 'lastMonth') {
            const first = new Date(y, m - 1, 1);
            const last = new Date(y, m, 0);
            setStartDate(fmt(first));
            setEndDate(fmt(last));
        } else if (preset === '3months') {
            const first = new Date(y, m - 2, 1);
            setStartDate(fmt(first));
            setEndDate(fmt(today));
        } else if (preset === 'year') {
            setStartDate(`${y}-01-01`);
            setEndDate(fmt(today));
        }
    };

    // 지점별 집계
    const locationStats = useMemo(() => {
        const map: Record<string, any> = {};
        rawData.forEach((r: any) => {
            const loc = r.location || 'unknown';
            if (!map[loc]) map[loc] = { id: loc, location: loc, applications: 0, count: 0, waiting: 0, registered: 0 };
            map[loc].applications++;                          // 상담신청수: 전체
            if (r.consultStatus === 1) map[loc].count++;     // 상담수: 진행만
            if (r.waitingMatchId) map[loc].waiting++;
            if (r.matchedStudentId) map[loc].registered++;
        });

        const rows = Object.values(map).map((r: any) => ({
            ...r,
            locationKorean: englishLocationToKorean(r.location) || r.location,
            applicationRegisterRate: r.applications > 0 ? +(r.registered / r.applications * 100).toFixed(1) : 0,
            waitingRate: r.count > 0 ? +(r.waiting / r.count * 100).toFixed(1) : 0,
            registerRate: r.count > 0 ? +(r.registered / r.count * 100).toFixed(1) : 0,
            waitingRegRate: r.waiting > 0 ? +(r.registered / r.waiting * 100).toFixed(1) : null,
        })).sort((a: any, b: any) => b.registerRate - a.registerRate);

        // 전사 합계 행
        const totalApplications = rows.reduce((s: number, r: any) => s + r.applications, 0);
        const totalCount = rows.reduce((s: number, r: any) => s + r.count, 0);
        const totalWaiting = rows.reduce((s: number, r: any) => s + r.waiting, 0);
        const totalRegistered = rows.reduce((s: number, r: any) => s + r.registered, 0);
        const totalRow = {
            id: '__total__',
            locationKorean: '전사 합계',
            isTotalRow: true,
            applications: totalApplications,
            count: totalCount,
            waiting: totalWaiting,
            registered: totalRegistered,
            applicationRegisterRate: totalApplications > 0 ? +(totalRegistered / totalApplications * 100).toFixed(1) : 0,
            waitingRate: totalCount > 0 ? +(totalWaiting / totalCount * 100).toFixed(1) : 0,
            registerRate: totalCount > 0 ? +(totalRegistered / totalCount * 100).toFixed(1) : 0,
            waitingRegRate: totalWaiting > 0 ? +(totalRegistered / totalWaiting * 100).toFixed(1) : null,
        };

        const avgAppRegisterRate = totalApplications > 0 ? +(totalRegistered / totalApplications * 100).toFixed(1) : 0;
        return { rows: [...rows, totalRow], avgRegisterRate: totalCount > 0 ? +(totalRegistered / totalCount * 100).toFixed(1) : 0, avgAppRegisterRate };
    }, [rawData]);

    // 담당자별 집계
    const assigneeStats = useMemo(() => {
        let filtered = rawData.filter((r: any) => r.assignedUserId);
        if (locationFilter !== 'total') {
            filtered = filtered.filter((r: any) => r.location === locationFilter);
        }

        const map: Record<string, any> = {};
        filtered.forEach((r: any) => {
            const key = `${r.assignedUserName || '미배정'}__${r.assignedUserId || 0}`;
            if (!map[key]) {
                map[key] = {
                    id: key,
                    assignedUserName: r.assignedUserName || '미배정',
                    location: r.location,
                    locationKorean: englishLocationToKorean(r.location) || r.location,
                    count: 0, waiting: 0, registered: 0,
                };
            }
            map[key].count++;
            if (r.waitingMatchId) map[key].waiting++;
            if (r.matchedStudentId) map[key].registered++;
        });

        const rows = Object.values(map).map((r: any) => ({
            ...r,
            waitingRate: r.count > 0 ? +(r.waiting / r.count * 100).toFixed(1) : 0,
            registerRate: r.count > 0 ? +(r.registered / r.count * 100).toFixed(1) : 0,
        })).sort((a: any, b: any) => b.registerRate - a.registerRate);

        const totalCount = rows.reduce((s: number, r: any) => s + r.count, 0);
        const totalRegistered = rows.reduce((s: number, r: any) => s + r.registered, 0);
        const avgRegisterRate = totalCount > 0 ? +(totalRegistered / totalCount * 100).toFixed(1) : 0;

        return { rows, avgRegisterRate };
    }, [rawData, locationFilter]);

    // startDate가 2026-05-01 이전이면 레거시 모드 (상담신청수·등록률만)
    const isLegacyMode = startDate < '2026-05-01';

    // 컬럼 — avgRef 객체로 평균값 공유 (renderCell 클로저)
    const locationAvgRef = { value: isLegacyMode ? locationStats.avgAppRegisterRate : locationStats.avgRegisterRate };
    const assigneeAvgRef = { value: assigneeStats.avgRegisterRate };

    const locationColumnBase = {
        field: 'locationKorean', headerName: '지점', width: 110,
        renderCell: (params: any) => (
            <span style={{ fontWeight: params.row.isTotalRow ? 'bold' : 'normal' }}>{params.value}</span>
        )
    };

    const locationColumns = isLegacyMode
        ? [
            locationColumnBase,
            { field: 'applications', headerName: '상담신청수', width: 100, type: 'number' as const },
            { field: 'registered', headerName: '최종등록', width: 90, type: 'number' as const },
            { field: 'applicationRegisterRate', headerName: '상담신청수→최종등록률', width: 180, type: 'number' as const, renderCell: makeRateCell(locationAvgRef, true) },
          ]
        : [
            locationColumnBase,
            { field: 'applications', headerName: '상담신청수', width: 100, type: 'number' as const },
            { field: 'count', headerName: '상담수', width: 80, type: 'number' as const },
            { field: 'waiting', headerName: '대기신청', width: 90, type: 'number' as const },
            { field: 'waitingRate', headerName: '상담→대기율', width: 130, type: 'number' as const, renderCell: makeRateCell(locationAvgRef) },
            { field: 'registered', headerName: '최종등록', width: 90, type: 'number' as const },
            { field: 'registerRate', headerName: '상담→최종등록률', width: 155, type: 'number' as const, renderCell: makeRateCell(locationAvgRef, true) },
            { field: 'waitingRegRate', headerName: '대기→최종등록률', width: 155, type: 'number' as const, renderCell: makeRateCell(locationAvgRef) },
          ];

    const assigneeColumns = [
        { field: 'assignedUserName', headerName: '담당자', width: 100 },
        { field: 'locationKorean', headerName: '지점', width: 100 },
        { field: 'count', headerName: '상담수', width: 80, type: 'number' as const },
        { field: 'waiting', headerName: '대기신청', width: 90, type: 'number' as const },
        { field: 'waitingRate', headerName: '상담→대기율', width: 130, type: 'number' as const, renderCell: makeRateCell(assigneeAvgRef) },
        { field: 'registered', headerName: '등록', width: 80, type: 'number' as const },
        { field: 'registerRate', headerName: '상담→최종등록률', width: 155, type: 'number' as const, renderCell: makeRateCell(assigneeAvgRef, true) },
    ];

    const rows = type === 'location' ? locationStats.rows : assigneeStats.rows;
    const columns = type === 'location' ? locationColumns : assigneeColumns;

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ minWidth: 860, maxHeight: '90vh', overflow: 'auto' }}>
                {/* 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>
                        {type === 'location' ? '지점별 방문 상담 통계' : '담당자별 방문 상담 통계'}
                    </span>
                    <span style={{ color: '#e65100', fontSize: 12 }}>⚠️ 최근 데이터는 잠정치일 수 있습니다.</span>
                </div>

                {/* 빠른 선택 버튼 */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                    {[
                        { label: '이번 주', key: 'week' },
                        { label: '이번 달', key: 'month' },
                        { label: '지난 달', key: 'lastMonth' },
                        { label: '최근 3개월', key: '3months' },
                        { label: '올해', key: 'year' },
                    ].map(({ label, key }) => (
                        <Button key={key} size="sm" variant="soft" color="neutral" onClick={() => setQuick(key)}>
                            {label}
                        </Button>
                    ))}
                </div>

                {/* 기간 선택 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 13 }}>기간</span>
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13 }}
                    />
                    <span style={{ fontSize: 13 }}>~</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: 5, fontSize: 13 }}
                    />
                </div>

                {/* 담당자 모달 - 지점 필터 */}
                {type === 'assignee' && (
                    <div style={{ marginBottom: 12 }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={locationFilter}
                            onChange={(_: any, v: string) => { if (v) setLocationFilter(v); }}
                        >
                            <ToggleButton value="total">전체</ToggleButton>
                            {filteredLocations.map((l: any) => (
                                <ToggleButton key={l.english} value={l.english}>{l.korean}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </div>
                )}

                {/* 통계 테이블 */}
                <div style={{ height: 480, backgroundColor: 'white' }}>
                    <DataGridPremium
                        rows={rows}
                        columns={columns}
                        density="compact"
                        loading={loading}
                        getRowClassName={(params: any) =>
                            params.row.isTotalRow ? 'total-row' : ''
                        }
                        sx={{
                            '& .total-row': {
                                backgroundColor: '#f5f5f5',
                                fontWeight: 'bold',
                            }
                        }}
                        hideFooter
                    />
                </div>

                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </ModalDialog>
        </Modal>
    );
};

export default ConsultStatsModal;
